import React from 'react'
import styled from 'styled-components'
import LinkHandler from '../utils/LinkHandler'
import { SIZES } from '../../constants'

const Wrapper = styled.div`
  width: 100%;
  position: relative;
  background-color: #F2F3F8;
  background-repeat: no-repeat;
  border-radius: 8px;
  box-sizing: border-box;
  font-family: Roboto, sans-serif;
  color: #000000;
  font-size: 14px;
  margin-bottom: 20px;
  padding: 16px 16px 0 16px;
  overflow: hidden;

  &:before {
    content: "";
    position: absolute;
    top: -40px;
    right: -8px;
    width: 82px;
    height: 83px;
    display: block;
    background-image: url('${STATICS_PATH}images/plague/background.jpg');
    transform: scaleX(-1);
    background-position: 0 0;
  }

  @media (max-width: ${SIZES.BREAKPOINT_MD - 1}px) {
    margin-top: 20px;
  }
  
  @media (max-width: ${SIZES.BREAKPOINT_SM - 1}px) {
    padding-bottom: 0;
  }
`

const MainBoxItem = styled.div`
  margin-top: 10px;
  flex: 0 0 70%;
  margin-bottom: 16px;

  @media (max-width: ${SIZES.BREAKPOINT_SM - 1}px) {
    margin-top: 0;
    flex: 0 0 100%;
    padding-left: 0;
  }
`

const InformationLink = styled.a`
  font-weight: bold;
  font-size: 21px;
  line-height: 21px;
  width: 100%;
  color: #000000;

  @media (max-width: ${SIZES.BREAKPOINT_XS - 1}px) {
    font-size: 21px;
  }
  
  &:hover {
   color: ${props => props.theme.covid.textHover};
  }
`

const InformationButtonsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`

const InformationButton = styled.a`
  font-size: 13px;
  color: #2F6894;
  text-decoration: none;
  border: 1px solid #cfdde8;
  box-sizing: border-box;
  border-radius: 4px;
  padding: 4px 7px;
  margin: 6px 3px 0 0;
  position: relative;
  z-index: 99;
  background: #e8f1f8;
  font-weight: bold;
  
  @media (max-width: ${SIZES.BREAKPOINT_XS - 1}px) {
    font-size: 10px;
  }
  
  &:hover {
   color: ${props => props.theme.covid.textHover};
   text-decoration: none;
  }
`

const DataWrapper = styled.div`
  text-align: center;
  position: relative;
  display: flex;
  width: 100%;
  flex-wrap: nowrap;
  z-index: 1;

  @media (max-width: ${SIZES.BREAKPOINT_XS - 1}px) {
    justify-content: space-between;
  }
`

const DataItem = styled.div`
  display: block;
  flex-grow: 1;
  text-align: left;
  box-sizing: border-box;
  color: #000000;

  @media (max-width: ${SIZES.BREAKPOINT_XS - 1}px) {
    flex: 0 0 auto;
  }
`

const DataNewCases = styled.h3`
  font-weight: bold;
  font-size: 21px;
  line-height: 21px;
  margin-bottom: 0;

  @media (max-width: ${SIZES.BREAKPOINT_XS - 1}px) {
    font-size: 14px;
    line-height: 14px;
  }
`

const DataAllCases = styled.p`
  font-size: 13px;
  margin-bottom: 0;
  
  @media (max-width: ${SIZES.BREAKPOINT_XS - 1}px) {
    font-size: 9px;
    line-height: 13px;
  }
`

const HospitalizedNumber = styled.span`
  color: #48A9A6;
`

const SickNumber = styled.span`
  color: #d31818;
`
const HealthyNumber = styled.span`
  color: #287e05;
`

const DataKey = styled.em`
  font-weight: normal;
  font-size: 13px;
  line-height: 24px;
  font-style: normal;

  @media (max-width: ${SIZES.BREAKPOINT_XS - 1}px) {
    font-size: 10px;
    line-height: 10px;
  }
`

const partitionBySource = (items, source) => {
  const noMatch = []
  const match = []

  items.forEach(item => item.source === source ? match.push(item) : noMatch.push(item))

  return [ noMatch ]
}

const formatNumber = (number) => number.toLocaleString('en').replace(/,/g, ' ')

export default (props) => {
  const {
    widget: {
      utmSource,
      utmMedium,
      boxes
    }
  } = props
  const informationButtonUrl = 'https://zpravy.aktualne.cz/widget-koronavirus-vse-o-koronaviru/r~28c952346d0611eaa6f6ac1f6b220ee8/'
  const informationButtonLink = new LinkHandler(informationButtonUrl).mergeUTMParams({ utmSource, utmMedium, utmContent: 'covid' }).link
  const worldDataLink = 'https://koronavirus.mzcr.cz/seznam-zemi-podle-miry-rizika-nakazy/'
  const situationInCRLink = 'https://onemocneni-aktualne.mzcr.cz/covid-19/stupne-pohotovosti'
  const [ [ stats ] ] = partitionBySource(boxes, 'BbxCoronavirus')

  return (
    <Wrapper>
      <InformationLink href={informationButtonLink}>Vše důležité o koronaviru</InformationLink>
      <MainBoxItem>
        <DataWrapper>
          <DataItem>
            <DataKey>Nakažených</DataKey>
            <SickNumber>
              <DataNewCases>+{ formatNumber(stats.countSick - stats.previousCountSick) }</DataNewCases>
              <DataAllCases>{formatNumber(stats.countSick)}</DataAllCases>
            </SickNumber>
          </DataItem>
          <DataItem>
            <DataKey>Uzdravených</DataKey>
            <HealthyNumber>
              <DataNewCases>+{ formatNumber(stats.countRecover - stats.previousCountRecover) }</DataNewCases>
              <DataAllCases>{formatNumber(stats.countRecover)}</DataAllCases>
            </HealthyNumber>
          </DataItem>
          <DataItem>
            <DataKey>Hospitalizovaných</DataKey>
            <HospitalizedNumber>
              <DataNewCases>{formatNumber(stats.countHospitalized)}</DataNewCases>
            </HospitalizedNumber>
          </DataItem>
          <DataItem>
            <DataKey>Úmrtí</DataKey>
            <DataNewCases>+{ formatNumber(stats.countDead - stats.previousCountDead) }</DataNewCases>
            <DataAllCases>{formatNumber(stats.countDead)}</DataAllCases>
          </DataItem>
        </DataWrapper>
        <InformationButtonsContainer>
          <InformationButton href={informationButtonLink} target='_blank'>» Počty nakažených v ČR i ve světě a další statistiky</InformationButton>
          <InformationButton href={worldDataLink} target='_blank'>» Riziko nákazy v zahraničí</InformationButton>
          <InformationButton href={situationInCRLink} target='_blank'>» Míra rizika nákazy v Česku</InformationButton>
        </InformationButtonsContainer>
      </MainBoxItem>
    </Wrapper>
  )
}
